import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGauge, faCartShopping, faChartLine, faDatabase, faRightFromBracket, faChartBar, faBars, faClipboardList, faBell, faUserCircle, faCheckDouble, faChartColumn, faTableCells, faCalculator, faVolumeHigh, faVolumeXmark, faPlay } from '@fortawesome/free-solid-svg-icons';
import { getNotifications, saveNotifications } from '../utils/notifications';
import { getSoundSettings, saveSoundSettings, playSound } from '../utils/sounds';

export const items = [
  ['/', faGauge, 'Dashboard'],
  ['/watchlist', faDatabase, 'Watchlist'],
  ['/option-chain', faTableCells, 'Option Chain'],
  ['/order-book', faClipboardList, 'Order Book'],
  ['/position', faChartLine, 'Positions'],
  ['/pnl', faChartColumn, 'PNL'],
  ['/alerts', faBell, 'Alerts'],
  ['/calculator', faCalculator, 'Calculator']
];
export const mobileItems = items.filter(([to]) => !['/pnl','/alerts','/calculator','/option-chain'].includes(to));

function useLogout() {
  const navigate = useNavigate();
  return useCallback(async () => {
    if (!window.confirm('Are you sure you want to logout?')) return;
    try { await fetch(`${process.env.REACT_APP_API_URL || ''}/api/logout`, { method: 'POST', credentials: 'include' }); } catch (error) {}
    ['kotak_trading_token','kotak_sid1','kotak_sid2','kotak_auth','kotak_baseurl','kotak_server_id','kotak_user'].forEach((key) => localStorage.removeItem(key));
    navigate('/login');
  }, [navigate]);
}

export function TerminalSidebar({ expanded, onNavigate }) {
  const logout = useLogout();
  return (
    <aside className={`mk-sidebar-grid terminal-sidebar compact ${expanded ? 'expanded' : ''}`}>
      <div className="sidebar-brand" title="MK Trading"><b>MK</b><span>Trading</span></div>
      <div className="sidebar-section-label">Terminal</div>
      <nav>
        {items.map(([to, icon, label]) => (
          <NavLink key={to} to={to} end={to === '/'} title={label} onClick={onNavigate}>
            <FontAwesomeIcon icon={icon}/><span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-spacer"/>
      <div className="sidebar-connection"><i/><span>Kotak Live</span></div>
      <button className="logout-btn" onClick={logout}><FontAwesomeIcon icon={faRightFromBracket}/><span>Logout</span></button>
    </aside>
  );
}

export function TerminalHeader({ onToggle }) {
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [soundOpen, setSoundOpen] = useState(false);
  const [soundSettings, setSoundSettingsState] = useState(getSoundSettings());
  const [clock, setClock] = useState(new Date());
  const [notifications, setNotifications] = useState(getNotifications());
  const notificationRef = useRef(null);
  const soundRef = useRef(null);
  const userName = localStorage.getItem('kotak_user') || 'Trader';
  const unread = useMemo(() => notifications.filter((item) => !item.read).length, [notifications]);
  const refreshNotifications = useCallback(() => setNotifications(getNotifications()), []);
  const logout = useLogout();
  const accountKey = localStorage.getItem('kotak_ucc') || localStorage.getItem('kotak_mobile') || 'default';
  const syncServerNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/notifications?accountKey=${encodeURIComponent(accountKey)}&limit=30`, { credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      const serverItems = (data.notifications || []).map(item => ({
        id: `server-${item.id}`, serverId: item.id, title: item.title, message: item.message,
        type: item.type === 'PRICE_ALERT' ? 'success' : 'info', read: Boolean(item.isRead), createdAt: item.createdAt
      }));
      const current = getNotifications();
      const localOnly = current.filter(item => !item.serverId);
      const merged = [...serverItems, ...localOnly].sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0,50);
      saveNotifications(merged);
      setNotifications(merged);
    } catch (error) {}
  }, [accountKey]);

  useEffect(() => { const id = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(id); }, []);
  useEffect(() => {
    syncServerNotifications();
    const id = setInterval(syncServerNotifications, 5000);
    window.addEventListener('mk-server-notifications-changed', syncServerNotifications);
    window.addEventListener('mk-notifications-changed', refreshNotifications);
    window.addEventListener('storage', refreshNotifications);
    return () => { clearInterval(id); window.removeEventListener('mk-server-notifications-changed', syncServerNotifications); window.removeEventListener('mk-notifications-changed', refreshNotifications); window.removeEventListener('storage', refreshNotifications); };
  }, [refreshNotifications, syncServerNotifications]);
  useEffect(() => {
    const close = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) setNoticeOpen(false);
      if (soundRef.current && !soundRef.current.contains(event.target)) setSoundOpen(false);
    };
    const syncSounds = () => setSoundSettingsState(getSoundSettings());
    document.addEventListener('mousedown', close);
    window.addEventListener('mk-sound-settings-changed', syncSounds);
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('mk-sound-settings-changed', syncSounds); };
  }, []);
  const updateSoundSetting = (key, value) => {
    const next = saveSoundSettings({ ...soundSettings, [key]: value });
    setSoundSettingsState(next);
  };
  const markAllRead = async () => {
    const next = notifications.map((item) => ({ ...item, read: true })); setNotifications(next); saveNotifications(next);
    try { await fetch(`${process.env.REACT_APP_API_URL || ''}/api/notifications/read-all`, { method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include', body:JSON.stringify({accountKey}) }); } catch (error) {}
  };
  const clearAll = async () => {
    // Delete server-backed notifications first so the 5-second sync cannot restore them.
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/notifications/clear-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ accountKey })
      });
      if (!response.ok) throw new Error('Server could not clear notifications.');
      setNotifications([]);
      saveNotifications([]);
    } catch (error) {
      // Keep the notification list visible if the database delete failed.
      console.error('[NOTIFICATIONS CLEAR ALL]', error);
      window.alert('Unable to clear notifications from the database. Please try again.');
    }
  };

  return (
    <header className="terminal-topbar">
      <div className="terminal-topbar-title">
        <button className="sidebar-toggle" onClick={onToggle} aria-label="Toggle navigation"><FontAwesomeIcon icon={faBars}/></button>
      </div>
      <div className="topbar-actions">
        <div className="terminal-clock" aria-label="Current time">{clock.toLocaleTimeString('en-IN',{hour:'numeric',minute:'2-digit',second:'2-digit',hour12:true})}</div>
        <div className="sound-settings-wrap" ref={soundRef}>
          <button type="button" className="sound-settings-btn" onClick={() => { setSoundOpen(v => !v); setNoticeOpen(false); }} aria-label="Sound notification settings" title="Sound settings"><FontAwesomeIcon icon={soundSettings.enabled ? faVolumeHigh : faVolumeXmark}/></button>
          {soundOpen && <div className="sound-settings-panel">
            <div className="sound-settings-heading"><div><strong>Sound Notifications</strong><small>Trading alerts and order feedback</small></div><span className={soundSettings.enabled ? 'sound-status-on' : 'sound-status-off'}>{soundSettings.enabled ? 'On' : 'Off'}</span></div>
            <label className="sound-setting-row sound-master-row"><span><b>Enable sounds</b><small>Master sound switch</small></span><input type="checkbox" checked={Boolean(soundSettings.enabled)} onChange={e => updateSoundSetting('enabled', e.target.checked)}/></label>
            <label className="sound-setting-row"><span><b>Price alerts</b><small>When a configured price alert triggers</small></span><input type="checkbox" checked={Boolean(soundSettings.priceAlerts)} onChange={e => updateSoundSetting('priceAlerts', e.target.checked)}/></label>
            <label className="sound-setting-row"><span><b>Order success</b><small>After successful order placement</small></span><input type="checkbox" checked={Boolean(soundSettings.orderSuccess)} onChange={e => updateSoundSetting('orderSuccess', e.target.checked)}/></label>
            <label className="sound-setting-row"><span><b>Order failure</b><small>When order placement fails</small></span><input type="checkbox" checked={Boolean(soundSettings.orderFailure)} onChange={e => updateSoundSetting('orderFailure', e.target.checked)}/></label>
            <label className="sound-volume-row"><span>Volume</span><input type="range" min="0.1" max="1" step="0.1" value={Number(soundSettings.volume) || 0.35} onChange={e => updateSoundSetting('volume', Number(e.target.value))}/><b>{Math.round((Number(soundSettings.volume) || 0.35) * 100)}%</b></label>
            <button type="button" className="sound-test-btn" onClick={() => playSound('priceAlert', true)}><FontAwesomeIcon icon={faPlay}/> Test Sound</button>
            <small className="sound-browser-note">If sound is blocked, tap Test Sound once to allow audio playback.</small>
          </div>}
        </div>
        <div className="notification-wrap" ref={notificationRef}>
          <button className="notification-btn" onClick={() => { setNoticeOpen((v) => !v); setSoundOpen(false); }} aria-label="Notifications"><FontAwesomeIcon icon={faBell}/>{unread > 0 && <b>{unread > 9 ? '9+' : unread}</b>}</button>
          {noticeOpen && <div className="notification-panel"><div className="notification-head"><strong>Notifications</strong><div className="notification-head-actions"><button onClick={markAllRead}><FontAwesomeIcon icon={faCheckDouble}/> Mark read</button><button className="clear-all-btn" onClick={clearAll}>Clear all</button></div></div>{notifications.length ? <div className="notification-list">{notifications.map(item => <button key={item.id} className={item.read ? '' : 'unread'} onClick={async () => { const next = notifications.map(entry => entry.id === item.id ? {...entry,read:true} : entry); setNotifications(next); saveNotifications(next); if (item.serverId) { try { await fetch(`${process.env.REACT_APP_API_URL || ''}/api/notifications/${item.serverId}/read`, { method:'POST', headers:{'Content-Type':'application/json'}, credentials:'include', body:JSON.stringify({accountKey}) }); } catch (error) {} } }}><b>{item.title}</b><span>{item.message}</span><small>{new Date(item.createdAt).toLocaleString()}</small></button>)}</div> : <div className="notification-empty">No new notifications</div>}</div>}
        </div>
        <div className="header-greeting"><FontAwesomeIcon icon={faUserCircle}/><span>Hi, <b>{userName}</b></span></div>
        <button className="top-logout-btn" onClick={logout}><FontAwesomeIcon icon={faRightFromBracket}/><span>Logout</span></button>
      </div>
    </header>
  );
}

export function MobileBottomNav() {
  return <nav className="mobile-bottom-nav">{mobileItems.map(([to,icon,label]) => <NavLink key={to} to={to} end={to === '/'}><FontAwesomeIcon icon={icon}/><span>{label}</span></NavLink>)}</nav>;
}
